import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Droplets, 
  Zap, 
  DollarSign, 
  Activity,
  Brain,
  RefreshCw,
  CheckCircle,
  Clock,
  Target,
  BarChart3,
  Gauge
} from 'lucide-react';
import api from '../services/api';

const PredictiveAnalytics = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [summaryData, setSummaryData] = useState(null);
  const [leakRiskData, setLeakRiskData] = useState(null);
  const [demandData, setDemandData] = useState(null);
  const [equipmentData, setEquipmentData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [nrwData, setNrwData] = useState(null);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [summary, leak, demand, equipment, revenue, nrw] = await Promise.all([
        api.client.get('/predictive/summary'),
        api.client.get('/predictive/leak-risk'),
        api.client.get('/predictive/demand-forecast'),
        api.client.get('/predictive/equipment-failure'),
        api.client.get('/predictive/revenue-forecast'),
        api.client.get('/predictive/nrw-trends')
      ]);
      
      setSummaryData(summary.data.data);
      setLeakRiskData(leak.data.data);
      setDemandData(demand.data.data);
      setEquipmentData(equipment.data.data);
      setRevenueData(revenue.data.data);
      setNrwData(nrw.data.data);
    } catch (error) {
      console.error('Error fetching predictive data:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    setRefreshing(true);
    await fetchAllData();
    setRefreshing(false);
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const getRiskBadge = (level) => {
    const colors = {
      'Critical': 'bg-red-500',
      'High': 'bg-orange-500',
      'Medium': 'bg-yellow-500',
      'Low': 'bg-green-500'
    };
    return <Badge className={`${colors[level] || 'bg-gray-500'} text-white`}>{level}</Badge>;
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-green-500';
    if (score >= 60) return 'text-yellow-500';
    if (score >= 40) return 'text-orange-500';
    return 'text-red-500';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="flex flex-col items-center gap-4">
          <Brain className="w-16 h-16 text-cyan-500 animate-pulse" />
          <p className="text-cyan-400 text-lg">AI analyzing system data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="predictive-analytics">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Brain className="w-8 h-8 text-cyan-400" />
            Predictive Analytics
          </h1>
          <p className="text-gray-400 mt-1">AI-powered predictions and risk analysis</p>
        </div>
        <Button 
          onClick={refreshData} 
          disabled={refreshing}
          className="bg-cyan-600 hover:bg-cyan-700"
          data-testid="refresh-predictions-btn"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Analyzing...' : 'Refresh Predictions'}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-6 bg-slate-800 mb-6">
          <TabsTrigger value="summary" className="data-[state=active]:bg-cyan-600">
            <Activity className="w-4 h-4 mr-2" />Summary
          </TabsTrigger>
          <TabsTrigger value="leak-risk" className="data-[state=active]:bg-cyan-600">
            <Droplets className="w-4 h-4 mr-2" />Leak Risk
          </TabsTrigger>
          <TabsTrigger value="demand" className="data-[state=active]:bg-cyan-600">
            <BarChart3 className="w-4 h-4 mr-2" />Demand
          </TabsTrigger>
          <TabsTrigger value="equipment" className="data-[state=active]:bg-cyan-600">
            <Zap className="w-4 h-4 mr-2" />Equipment
          </TabsTrigger>
          <TabsTrigger value="revenue" className="data-[state=active]:bg-cyan-600">
            <DollarSign className="w-4 h-4 mr-2" />Revenue
          </TabsTrigger>
          <TabsTrigger value="nrw" className="data-[state=active]:bg-cyan-600">
            <Gauge className="w-4 h-4 mr-2" />NRW
          </TabsTrigger>
        </TabsList>

        {/* Summary Tab */}
        <TabsContent value="summary" data-testid="summary-tab">
          {summaryData && (
            <div className="space-y-6">
              {/* Health Score */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700 col-span-1">
                  <CardContent className="p-6 text-center">
                    <div className={`text-5xl font-bold ${getHealthColor(summaryData.health_score)}`}>
                      {summaryData.health_score}
                    </div>
                    <p className="text-gray-400 mt-2">System Health Score</p>
                    <Badge className={`mt-2 ${summaryData.health_status === 'Good' ? 'bg-green-600' : 'bg-yellow-600'}`}>
                      {summaryData.health_status}
                    </Badge>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700 col-span-3">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-orange-400" />
                      Top Risk Areas
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summaryData.risk_areas?.map((risk, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                          <div>
                            <span className="text-white font-medium">{risk.area}</span>
                            <p className="text-gray-400 text-sm">{risk.impact}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="text-gray-400">{risk.probability}%</span>
                            {getRiskBadge(risk.risk_level)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* 30-Day Predictions */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Target className="w-5 h-5 text-cyan-400" />
                    30-Day Predictions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {summaryData.predictions_30_day?.map((pred, idx) => (
                      <div key={idx} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex items-start justify-between">
                          <p className="text-white">{pred.prediction}</p>
                          <Badge className="bg-cyan-600 ml-2">{pred.confidence}%</Badge>
                        </div>
                        <p className="text-gray-400 text-sm mt-2">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {pred.timeframe}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Priority Actions */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-400" />
                    Priority Actions
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {summaryData.priority_actions?.map((action, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-700/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            action.urgency === 'Immediate' ? 'bg-red-500' :
                            action.urgency === 'High' ? 'bg-orange-500' :
                            action.urgency === 'Medium' ? 'bg-yellow-500' : 'bg-green-500'
                          }`} />
                          <div>
                            <span className="text-white">{action.action}</span>
                            <p className="text-gray-400 text-sm">{action.impact}</p>
                          </div>
                        </div>
                        <Badge variant="outline" className="border-gray-600 text-gray-300">
                          {action.urgency}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Leak Risk Tab */}
        <TabsContent value="leak-risk" data-testid="leak-risk-tab">
          {leakRiskData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <Droplets className="w-10 h-10 text-blue-400 mx-auto" />
                    <div className={`text-4xl font-bold mt-2 ${getHealthColor(100 - leakRiskData.overall_risk_score)}`}>
                      {leakRiskData.overall_risk_score}%
                    </div>
                    <p className="text-gray-400">Overall Leak Risk</p>
                  </CardContent>
                </Card>
                
                {Object.entries(leakRiskData.risk_factors_analysis || {}).map(([key, value]) => (
                  <Card key={key} className="bg-slate-800 border-slate-700">
                    <CardContent className="p-4">
                      <p className="text-gray-400 text-sm capitalize">{key.replace(/_/g, ' ')}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 bg-slate-700 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full ${value.score > 60 ? 'bg-red-500' : value.score > 40 ? 'bg-yellow-500' : 'bg-green-500'}`}
                            style={{ width: `${value.score}%` }}
                          />
                        </div>
                        <span className="text-white font-medium">{value.score}</span>
                      </div>
                      <p className="text-gray-500 text-xs mt-1">{value.details}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-red-400">High Risk Areas</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {leakRiskData.high_risk_areas?.map((area, idx) => (
                        <div key={idx} className="p-3 bg-red-900/20 border border-red-800/50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <span className="text-white font-medium">{area.area}</span>
                            <Badge className="bg-red-500">{area.risk_score}%</Badge>
                          </div>
                          <div className="mt-2 text-sm text-gray-400">
                            <p>Predicted leaks: {area.predicted_leaks}</p>
                            <p>Est. loss: {area.estimated_loss_kl_day} KL/day</p>
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {area.factors?.map((f, i) => (
                              <Badge key={i} variant="outline" className="text-xs border-red-700 text-red-300">{f}</Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-green-400">Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {leakRiskData.recommendations?.map((rec, idx) => (
                        <div key={idx} className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg">
                          <CheckCircle className="w-5 h-5 text-green-400 mt-0.5" />
                          <span className="text-white">{rec}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Demand Forecast Tab */}
        <TabsContent value="demand" data-testid="demand-tab">
          {demandData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Current Demand</p>
                    <div className="text-3xl font-bold text-cyan-400 mt-2">
                      {demandData.current_daily_demand_mld} MLD
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">7-Day Avg Forecast</p>
                    <div className="text-3xl font-bold text-blue-400 mt-2">
                      {demandData.forecast_7_day?.average_demand_mld?.toFixed(1)} MLD
                    </div>
                    <Badge className="mt-2 bg-blue-600">{demandData.forecast_7_day?.confidence}% confidence</Badge>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Peak Forecast</p>
                    <div className="text-3xl font-bold text-orange-400 mt-2">
                      {demandData.forecast_7_day?.peak_demand_mld?.toFixed(1)} MLD
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Capacity Buffer</p>
                    <div className="text-3xl font-bold text-green-400 mt-2">
                      {demandData.capacity_analysis?.buffer_available_mld} MLD
                    </div>
                    <Badge className={`mt-2 ${demandData.capacity_analysis?.risk_of_shortage === 'Low' ? 'bg-green-600' : 'bg-yellow-600'}`}>
                      {demandData.capacity_analysis?.risk_of_shortage} Risk
                    </Badge>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">7-Day Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {demandData.forecast_7_day?.daily_forecast?.map((day, idx) => (
                        <div key={idx} className="flex items-center justify-between p-2 bg-slate-700/50 rounded">
                          <span className="text-white">{day.day}</span>
                          <div className="flex items-center gap-2">
                            <div className="w-32 bg-slate-600 rounded-full h-2">
                              <div 
                                className="bg-cyan-500 h-2 rounded-full"
                                style={{ width: `${(day.demand_mld / 60) * 100}%` }}
                              />
                            </div>
                            <span className="text-cyan-400 font-medium w-16 text-right">{day.demand_mld} MLD</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Seasonal Factors</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {demandData.seasonal_factors?.map((factor, idx) => (
                        <div key={idx} className="flex items-center justify-between">
                          <span className="text-gray-300">{factor.factor}</span>
                          <div className="flex items-center gap-2">
                            {factor.impact_percentage > 0 ? (
                              <TrendingUp className="w-4 h-4 text-red-400" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-green-400" />
                            )}
                            <span className={factor.impact_percentage > 0 ? 'text-red-400' : 'text-green-400'}>
                              {factor.impact_percentage > 0 ? '+' : ''}{factor.impact_percentage}%
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* Equipment Failure Tab */}
        <TabsContent value="equipment" data-testid="equipment-tab">
          {equipmentData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <Zap className="w-10 h-10 text-yellow-400 mx-auto" />
                    <div className={`text-4xl font-bold mt-2 ${getHealthColor(equipmentData.overall_equipment_health)}`}>
                      {equipmentData.overall_equipment_health}%
                    </div>
                    <p className="text-gray-400">Equipment Health</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Preventive Cost</p>
                    <div className="text-2xl font-bold text-green-400 mt-2">
                      {formatCurrency(equipmentData.cost_benefit_analysis?.preventive_maintenance_cost || 0)}
                    </div>
                    <p className="text-xs text-gray-500">Recommended investment</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Potential Failure Cost</p>
                    <div className="text-2xl font-bold text-red-400 mt-2">
                      {formatCurrency(equipmentData.cost_benefit_analysis?.potential_failure_cost || 0)}
                    </div>
                    <p className="text-xs text-gray-500">If no action taken</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-red-400">High Risk Equipment</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-gray-400 text-sm border-b border-slate-700">
                          <th className="text-left p-2">Equipment</th>
                          <th className="text-left p-2">Location</th>
                          <th className="text-left p-2">Failure Mode</th>
                          <th className="text-center p-2">Probability</th>
                          <th className="text-left p-2">Timeframe</th>
                          <th className="text-right p-2">Est. Cost</th>
                        </tr>
                      </thead>
                      <tbody>
                        {equipmentData.high_risk_equipment?.map((eq, idx) => (
                          <tr key={idx} className="border-b border-slate-700/50">
                            <td className="p-2">
                              <span className="text-white font-medium">{eq.equipment_id}</span>
                              <p className="text-gray-500 text-xs">{eq.type}</p>
                            </td>
                            <td className="p-2 text-gray-300">{eq.location}</td>
                            <td className="p-2 text-gray-300">{eq.failure_mode}</td>
                            <td className="p-2 text-center">
                              <Badge className="bg-red-500">{eq.failure_probability}%</Badge>
                            </td>
                            <td className="p-2 text-orange-400">{eq.predicted_failure_timeframe}</td>
                            <td className="p-2 text-right text-white">{formatCurrency(eq.maintenance_cost_estimate)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Maintenance Recommendations</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {equipmentData.maintenance_recommendations?.map((rec, idx) => (
                      <div key={idx} className="p-4 bg-slate-700/50 rounded-lg">
                        <div className="flex justify-between items-start">
                          <span className="text-cyan-400 font-medium">{rec.equipment_type}</span>
                          <Badge variant="outline" className={`
                            ${rec.priority === 'High' ? 'border-red-500 text-red-400' : 
                              rec.priority === 'Medium' ? 'border-yellow-500 text-yellow-400' : 
                              'border-green-500 text-green-400'}
                          `}>{rec.priority}</Badge>
                        </div>
                        <p className="text-white mt-2">{rec.action}</p>
                        <p className="text-gray-400 text-sm mt-1">Prevents: {rec.prevents}</p>
                        <p className="text-green-400 mt-2">{formatCurrency(rec.estimated_cost)}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Revenue Forecast Tab */}
        <TabsContent value="revenue" data-testid="revenue-tab">
          {revenueData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Expected Billing</p>
                    <div className="text-2xl font-bold text-white mt-2">
                      {formatCurrency(revenueData.current_month?.expected_billing || 0)}
                    </div>
                    <p className="text-xs text-gray-500">This month</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Projected Collection</p>
                    <div className="text-2xl font-bold text-green-400 mt-2">
                      {formatCurrency(revenueData.current_month?.projected_collection || 0)}
                    </div>
                    <p className="text-xs text-gray-500">{revenueData.current_month?.projected_collection_rate?.toFixed(1)}% rate</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">High Risk Amount</p>
                    <div className="text-2xl font-bold text-red-400 mt-2">
                      {formatCurrency(revenueData.collection_risk_analysis?.high_risk_amount || 0)}
                    </div>
                    <p className="text-xs text-gray-500">{revenueData.collection_risk_analysis?.high_risk_accounts} accounts</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Revenue Leakage</p>
                    <div className="text-2xl font-bold text-orange-400 mt-2">
                      {formatCurrency(revenueData.revenue_leakage?.estimated_monthly_loss || 0)}
                    </div>
                    <p className="text-xs text-gray-500">Monthly loss</p>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">3-Month Forecast</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                        <span className="text-gray-300">Total Expected Billing</span>
                        <span className="text-white font-bold">{formatCurrency(revenueData.forecast_3_month?.total_expected_billing || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                        <span className="text-gray-300">Projected Collection</span>
                        <span className="text-green-400 font-bold">{formatCurrency(revenueData.forecast_3_month?.projected_collection || 0)}</span>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                        <span className="text-gray-300">Trend</span>
                        <div className="flex items-center gap-2">
                          <TrendingUp className="w-4 h-4 text-green-400" />
                          <span className="text-green-400">{revenueData.forecast_3_month?.trend}</span>
                        </div>
                      </div>
                      <div className="flex justify-between items-center p-3 bg-slate-700/50 rounded-lg">
                        <span className="text-gray-300">Confidence</span>
                        <Badge className="bg-cyan-600">{revenueData.forecast_3_month?.confidence}%</Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Action Recommendations</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {revenueData.recommendations?.map((rec, idx) => (
                        <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <span className="text-white">{rec.action}</span>
                            <Badge variant="outline" className={`
                              ${rec.priority === 'High' ? 'border-red-500 text-red-400' : 'border-yellow-500 text-yellow-400'}
                            `}>{rec.priority}</Badge>
                          </div>
                          <p className="text-green-400 text-sm mt-1">{rec.expected_impact}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>

        {/* NRW Trends Tab */}
        <TabsContent value="nrw" data-testid="nrw-tab">
          {nrwData && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <Gauge className="w-10 h-10 text-cyan-400 mx-auto" />
                    <div className="text-4xl font-bold text-cyan-400 mt-2">
                      {nrwData.current_nrw?.percentage}%
                    </div>
                    <p className="text-gray-400">Current NRW</p>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Volume Lost</p>
                    <div className="text-3xl font-bold text-blue-400 mt-2">
                      {nrwData.current_nrw?.volume_mld} MLD
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">Monthly Loss</p>
                    <div className="text-2xl font-bold text-red-400 mt-2">
                      {formatCurrency(nrwData.current_nrw?.financial_loss_monthly || 0)}
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800 border-slate-700">
                  <CardContent className="p-6 text-center">
                    <p className="text-gray-400">6-Month Target</p>
                    <div className="text-3xl font-bold text-green-400 mt-2">
                      {nrwData.forecast_6_month?.projected_nrw_percentage}%
                    </div>
                    <div className="flex items-center justify-center gap-1 mt-1">
                      <TrendingDown className="w-4 h-4 text-green-400" />
                      <span className="text-green-400 text-sm">{nrwData.trend_analysis?.direction}</span>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">NRW Components</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(nrwData.nrw_components || {}).map(([key, value]) => (
                        <div key={key}>
                          <div className="flex justify-between mb-1">
                            <span className="text-gray-300 capitalize">{key.replace(/_/g, ' ')}</span>
                            <span className="text-white font-medium">{value.percentage}%</span>
                          </div>
                          <div className="w-full bg-slate-700 rounded-full h-3">
                            <div 
                              className={`h-3 rounded-full ${
                                key === 'physical_losses' ? 'bg-red-500' :
                                key === 'commercial_losses' ? 'bg-orange-500' : 'bg-yellow-500'
                              }`}
                              style={{ width: `${(value.percentage / 18.5) * 100}%` }}
                            />
                          </div>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {value.causes?.map((cause, idx) => (
                              <Badge key={idx} variant="outline" className="text-xs border-gray-600 text-gray-400">{cause}</Badge>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-800 border-slate-700">
                  <CardHeader>
                    <CardTitle className="text-white">Reduction Opportunities</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {nrwData.reduction_opportunities?.map((opp, idx) => (
                        <div key={idx} className="p-3 bg-slate-700/50 rounded-lg">
                          <div className="flex justify-between items-start">
                            <span className="text-white">{opp.intervention}</span>
                            <Badge className="bg-green-600">-{opp.potential_reduction}%</Badge>
                          </div>
                          <div className="flex justify-between text-sm mt-2">
                            <span className="text-gray-400">Cost: {formatCurrency(opp.cost)}</span>
                            <span className="text-cyan-400">ROI: {opp.roi_months} months</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-white">Benchmarking</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                      <p className="text-gray-400">vs National Average</p>
                      <p className="text-green-400 font-medium mt-2">{nrwData.benchmarking?.current_vs_national_avg}</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                      <p className="text-gray-400">Target NRW</p>
                      <p className="text-cyan-400 text-2xl font-bold mt-2">{nrwData.benchmarking?.target_nrw}%</p>
                    </div>
                    <div className="p-4 bg-slate-700/50 rounded-lg text-center">
                      <p className="text-gray-400">Timeline to Target</p>
                      <p className="text-white font-medium mt-2">{nrwData.benchmarking?.timeline_to_target}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* AI Badge */}
      <div className="flex justify-center">
        <Badge className="bg-gradient-to-r from-purple-600 to-cyan-600 text-white px-4 py-2">
          <Brain className="w-4 h-4 mr-2" />
          Powered by Claude Sonnet AI
        </Badge>
      </div>
    </div>
  );
};

export default PredictiveAnalytics;
